import type { GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { Advisor } from '@/types'
import type { AdvisorFilterState } from '@/components/landing/advisors/advisor-filters'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBand } from '@/components/generals/cta-band'
import { PageHero } from '@/components/generals/page-hero'
import { PillButton } from '@/components/generals/pill-button'
import { Reveal } from '@/components/generals/reveal'
import { AdvisorCard } from '@/components/landing/advisors/advisor-card'
import { getAdvisors } from '@/lib/advisors'
import { availabilityTags } from '@/lib/schedule'
import { LANDSCAPES } from '@/lib/visual'

import {
    AdvisorFilters,
    AVAILABILITY_OPTIONS,
    DEFAULT_FILTERS,
    EXPERIENCE_OPTIONS
} from '@/components/landing/advisors/advisor-filters'

interface AdvisorsPageProps {
    advisors: Array<Advisor>
}

const AdvisorsPage: FC<AdvisorsPageProps> = ({ advisors }): ReactNode => {
    const router = useRouter()
    const [filters, setFilters] = useState<AdvisorFilterState>(DEFAULT_FILTERS)
    const specialties = useMemo(() => Array.from(new Set(advisors.map(advisor => advisor.specialty))).filter(Boolean).sort(), [advisors])

    useEffect(() => {
        if (!router.isReady) return

        const { specialty, availability } = router.query

        setFilters(current => ({
            ...current,
            specialty: typeof specialty === 'string' && specialties.includes(specialty) ? specialty : current.specialty,
            availability: typeof availability === 'string' && AVAILABILITY_OPTIONS.includes(availability) ? availability : current.availability
        }))
    }, [router.isReady, router.query, specialties])

    const filtered = useMemo(() => {
        const minYears = EXPERIENCE_OPTIONS.find(option => option.label === filters.experience)?.min ?? 0

        return advisors.filter(advisor => {
            if (filters.specialty !== DEFAULT_FILTERS.specialty && advisor.specialty !== filters.specialty) return false
            if (advisor.yearsExperience < minYears) return false
            if (filters.availability !== DEFAULT_FILTERS.availability && !availabilityTags(advisor).includes(filters.availability)) return false

            return true
        })
    }, [advisors, filters])

    return (
        <Layout>
            <Seo
                title='Advisors'
                description='Browse Verdane advisors, all certified and salaried, across retirement, investing, budgeting, home buying, tax and family wealth. Your first call is free.'
            />

            <PageHero
                eyebrow='Our advisors'
                title='Steady guidance for *every big decision*'
                subtitle='Each advisor is certified, on salary and earns no commission, whatever you choose. Narrow the list below, then book a free 30 minute call.'
                image={LANDSCAPES.wheatHaze}
                position='center 40%'
            />

            <section aria-label='Advisor directory' className='relative pb-28'>
                <div className='shell'>
                    <Reveal className='relative z-20'>
                        <AdvisorFilters specialties={specialties} value={filters} onChange={setFilters} resultCount={filtered.length} />
                    </Reveal>

                    {advisors.length === 0 && (
                        <div className='glass mx-auto mt-14 max-w-xl rounded-[28px] p-8 text-center'>
                            <p className='font-serif text-3xl italic text-cream'>
                                Back in a moment
                            </p>

                            <p className='mt-3 text-sm leading-relaxed text-cream/60'>
                                The advisor list did not load this time. Give it a moment and refresh, or answer a few questions and we will pair you up.
                            </p>

                            <PillButton href='/find-an-advisor' className='mt-6'>
                                Get matched
                            </PillButton>
                        </div>
                    )}

                    {advisors.length > 0 && filtered.length === 0 && (
                        <div className='glass mx-auto mt-14 max-w-xl rounded-[28px] p-8 text-center'>
                            <p className='font-serif text-3xl italic text-cream'>
                                Nobody fits just yet
                            </p>

                            <p className='mt-3 text-sm leading-relaxed text-cream/60'>
                                Those three filters together leave no one. Relax one of them, or take three short questions and we will point you to the nearest fit.
                            </p>

                            <div className='mt-6 flex flex-wrap items-center justify-center gap-3'>
                                <button
                                    type='button'
                                    onClick={() => setFilters(DEFAULT_FILTERS)}
                                    className='h-12 rounded-full px-5 text-sm text-cream ring-1 ring-white/20 transition-colors hover:bg-white/10'
                                >
                                    Reset filters
                                </button>

                                <PillButton href='/find-an-advisor'>
                                    Match me instead
                                </PillButton>
                            </div>
                        </div>
                    )}

                    <ul className='mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
                        {filtered.map((advisor, index) => (
                            <Reveal as='li' key={advisor.slug} delay={(index % 4) * 110} y={40}>
                                <AdvisorCard advisor={advisor} />
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <CtaBand
                eyebrow='Still deciding'
                title={'Three short questions,\n*one right person*'}
                buttonLabel='Get matched'
                href='/find-an-advisor'
                note='About a minute of your time. The first call costs nothing.'
                image='/images/landscapes/sunset-walk.jpg'
                position='center 55%'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AdvisorsPageProps> = async () => {
    try {
        const advisors = await getAdvisors()

        return { props: { advisors }, revalidate: 60 }
    }
    catch (error) {
        console.error('Advisors could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { advisors: [] }, revalidate: 15 }
    }
}

export default AdvisorsPage
